import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safe localStorage helpers (Safari Private Browsing throws on write)
const safeGetToken = () => {
  try { return localStorage.getItem('session_token'); } catch { return null; }
};
const safeSetToken = (token) => {
  try { localStorage.setItem('session_token', token); } catch { /* Safari Private Browsing */ }
};
const safeClearToken = () => {
  try { localStorage.removeItem('session_token'); } catch { /* Safari Private Browsing */ }
};

// In-memory fallback for when localStorage is unavailable
let memoryToken = null;

export const getSessionToken = () => safeGetToken() || memoryToken;
export const setSessionToken = (token) => { memoryToken = token; safeSetToken(token); };
export const clearSessionToken = () => { memoryToken = null; safeClearToken(); };

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add Bearer token from storage on every request
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Query param fallback for Safari — in case CORS blocks the Authorization header
    const separator = config.url?.includes('?') ? '&' : '?';
    config.url = `${config.url}${separator}_token=${token}`;
  }
  return config;
});

// Store token from auth responses
api.interceptors.response.use((response) => {
  if (response.data?.token) {
    setSessionToken(response.data.token);
  }
  return response;
});

// Clear token on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // If the user had a session token and it just got rejected, flag the login
      // page to show a helpful "session expired" toast. This handles Safari ITP
      // clearing localStorage after ~7 days, corporate proxies stripping auth
      // headers, and normal server-side session expiry.
      const hadToken = !!getSessionToken();
      clearSessionToken();
      if (hadToken) {
        try { sessionStorage.setItem('auth_expired_flag', '1'); } catch {}
      }
    }
    return Promise.reject(error);
  }
);

export { BACKEND_URL };
